const apiBaseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:65181'}${process.env.REACT_APP_API_VERSION === undefined ? '/api/v1' : process.env.REACT_APP_API_VERSION}`;
const SESSION_KEY = 'lingbi-analytics-session';
let activePage = '';
let activePageStartedAt = Date.now();
let leaveListenerInstalled = false;

interface AnalyticsPayload {
  event: string;
  page?: string;
  module?: string;
  action?: string;
  duration?: number;
  metadata?: Record<string, unknown>;
}

const createId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getAnalyticsSessionId = () => {
  const current = localStorage.getItem(SESSION_KEY);
  if (current) return current;
  const next = createId();
  localStorage.setItem(SESSION_KEY, next);
  return next;
};

const detectDevice = () => {
  const width = window.innerWidth || 0;
  if (width < 768) return 'mobile';
  if (width < 1180) return 'tablet';
  return 'desktop';
};

const requestHeaders = () => {
  const token = localStorage.getItem('lingbi-token');
  return {
    'Content-Type': 'application/json',
    ...antiBrushHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const antiBrushHeaders = () => ({
  'X-Session-ID': getAnalyticsSessionId(),
});

export const trackEvent = (payload: AnalyticsPayload) => {
  const body = JSON.stringify({
    page: window.location.hash || window.location.pathname || '/',
    module: 'web',
    action: 'view',
    duration: 0,
    session_id: getAnalyticsSessionId(),
    device: detectDevice(),
    platform: 'web',
    referrer: document.referrer || '',
    metadata: {},
    ...payload,
  });

  window.fetch(`${apiBaseUrl}/analytics/events`, {
    method: 'POST',
    headers: requestHeaders(),
    body,
    keepalive: true,
  }).catch(() => undefined);
};

export const identifyAnalyticsSession = (page?: string) => {
  const body = JSON.stringify({
    session_id: getAnalyticsSessionId(),
    page: page || window.location.hash || window.location.pathname || '/',
    device: detectDevice(),
    platform: 'web',
    referrer: document.referrer || '',
    metadata: {
      title: document.title,
    },
  });

  window.fetch(`${apiBaseUrl}/analytics/identify`, {
    method: 'POST',
    headers: requestHeaders(),
    body,
    keepalive: true,
  }).catch(() => undefined);
};

export const trackPageView = (page: string, metadata: Record<string, unknown> = {}) => {
  const now = Date.now();
  if (activePage && activePage !== page) {
    trackEvent({
      event: 'page_leave',
      page: activePage,
      module: 'navigation',
      action: 'leave',
      duration: Math.max(1, Math.round((now - activePageStartedAt) / 1000)),
    });
  }
  activePage = page;
  activePageStartedAt = now;
  if (!leaveListenerInstalled) {
    leaveListenerInstalled = true;
    window.addEventListener('pagehide', () => {
      if (!activePage) return;
      trackEvent({
        event: 'page_leave',
        page: activePage,
        module: 'navigation',
        action: 'leave',
        duration: Math.max(1, Math.round((Date.now() - activePageStartedAt) / 1000)),
      });
    });
  }
  trackEvent({
    event: 'page_view',
    page,
    module: 'navigation',
    action: 'view',
    metadata,
  });
};
